import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

// cleanFilters drops everything that is not actually set, so only real filters are sent
// (and counted).
export const cleanFilters = (filters) => {
  const out = {};
  Object.entries(filters || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v;
      return;
    }
    if (typeof v === 'object') {
      const inner = {};
      Object.entries(v).forEach(([ik, iv]) => {
        if (iv !== undefined && iv !== null && iv !== '') inner[ik] = iv;
      });
      if (Object.keys(inner).length) out[k] = inner;
      return;
    }
    out[k] = v;
  });
  return out;
};

export const countFilters = (filters) => {
  const c = cleanFilters(filters);
  let n = 0;
  Object.entries(c).forEach(([k, v]) => {
    if (k === 'preset') return;
    n += k === 'custom_fields' ? Object.keys(v).length : 1;
  });
  return n;
};

const Row = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>{children}</Box>
);

const Section = ({ title, count, defaultExpanded, children }) => (
  <Accordion disableGutters elevation={0} defaultExpanded={defaultExpanded} sx={{ borderBottom: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 44 }}>
      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{title}</Typography>
      {count > 0 && <Chip size="small" color="primary" label={count} sx={{ mr: 1, height: 20 }} />}
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pt: 0.5 }}>{children}</AccordionDetails>
  </Accordion>
);

const LeadFilters = ({ open, onClose, value, onApply, meta, segment, customColumns, isManager }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [draft, setDraft] = useState(value || {});

  useEffect(() => {
    if (open) setDraft(value || {});
  }, [open, value]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const field = { size: 'small', fullWidth: true };

  // Text / number / date inputs bound to a filter key.
  const text = (k, label, props = {}) => (
    <TextField {...field} label={label} value={draft[k] ?? ''} onChange={(e) => set(k, e.target.value)} {...props} />
  );
  const number = (k, label) => (
    <TextField
      {...field}
      label={label}
      type="number"
      value={draft[k] ?? ''}
      onChange={(e) => set(k, e.target.value === '' ? '' : Number(e.target.value))}
      inputProps={{ inputMode: 'numeric' }}
    />
  );
  const date = (k, label) => (
    <TextField {...field} label={label} type="date" value={draft[k] ?? ''} onChange={(e) => set(k, e.target.value)} InputLabelProps={{ shrink: true }} />
  );
  const select = (k, label, options) => (
    <TextField {...field} select label={label} value={draft[k] ?? ''} onChange={(e) => set(k, e.target.value)}>
      <MenuItem value="">Any</MenuItem>
      {options.map(([v, l]) => <MenuItem key={String(v)} value={v}>{l}</MenuItem>)}
    </TextField>
  );
  // Yes / No / Any for boolean filters.
  const yesNo = (k, label, yes = 'Yes', no = 'No') => (
    <TextField
      {...field}
      select
      label={label}
      value={draft[k] === true ? 'yes' : draft[k] === false ? 'no' : ''}
      onChange={(e) => set(k, e.target.value === 'yes' ? true : e.target.value === 'no' ? false : '')}
    >
      <MenuItem value="">Any</MenuItem>
      <MenuItem value="yes">{yes}</MenuItem>
      <MenuItem value="no">{no}</MenuItem>
    </TextField>
  );
  const onlyYes = (k, label) => (
    <TextField {...field} select label={label} value={draft[k] === true ? 'yes' : ''} onChange={(e) => set(k, e.target.value === 'yes' ? true : '')}>
      <MenuItem value="">Any</MenuItem>
      <MenuItem value="yes">Yes</MenuItem>
    </TextField>
  );
  const multi = (k, label, options) => (
    <TextField
      {...field}
      select
      label={label}
      value={draft[k] || []}
      onChange={(e) => set(k, typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
      SelectProps={{ multiple: true }}
    >
      {options.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
    </TextField>
  );

  const groupCount = (keys) => keys.filter((k) => {
    const v = draft[k];
    return !(v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
  }).length;

  const members = (meta?.members || []).filter((m) => m.active);
  const algoPlans = (meta?.algo_plans || []).map((p) => [p.key, p.label || p.key]);
  const cf = draft.custom_fields || {};
  const setCf = (name, v) => setDraft((d) => ({ ...d, custom_fields: { ...(d.custom_fields || {}), [name]: v } }));
  const cfCount = Object.values(cf).filter((v) => v !== '' && v !== undefined && v !== null).length;

  const body = (
    <Box sx={{ width: { xs: '100vw', sm: 440 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Filters</Typography>
        <IconButton onClick={onClose} aria-label="Close filters"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, WebkitOverflowScrolling: 'touch' }}>
        <Section title="Plans" defaultExpanded count={groupCount(['plan_status', 'plan_type', 'algo_plans', 'research_plan', 'expiring_in_days', 'expired_in_days', 'autopay', 'on_trial', 'unlocked', 'symbol'])}>
          <Row>
            {select('plan_status', 'Plan status', [['active', 'Active'], ['expired', 'Expired'], ['never', 'Never had a plan']])}
            {select('plan_type', 'Plan type', [['algo', 'Algo'], ['research', 'Research']])}
          </Row>
          <Row>
            {multi('algo_plans', 'Algo plan', algoPlans)}
            {select('research_plan', 'Research plan', (meta?.research_plans || []).map((p) => [p, p]))}
          </Row>
          <Row>
            {select('expiring_in_days', 'Expiring within', [[3, '3 days'], [7, '7 days'], [15, '15 days'], [30, '30 days'], [60, '60 days']])}
            {select('expired_in_days', 'Expired in last', [[7, '7 days'], [15, '15 days'], [30, '30 days'], [90, '90 days']])}
          </Row>
          <Row>
            {select('autopay', 'Autopay', [['active', 'Active'], ['inactive', 'Not active']])}
            {yesNo('on_trial', 'On trial')}
          </Row>
          <Row>
            {select('unlocked', 'Research unlocks used', [[-1, 'At least 1'], [0, 'None'], [1, '1'], [2, '2'], [3, '3']])}
            {text('symbol', 'Unlocked symbol')}
          </Row>
        </Section>

        <Section title="Payments" count={groupCount(['ever_paid', 'paid_from', 'paid_to', 'first_paid_from', 'first_paid_to', 'lifetime_paid_min', 'lifetime_paid_max', 'last_paid_min', 'last_paid_max', 'failed_payment', 'failed_from', 'failed_to', 'failed_amount_min', 'active_link', 'coupon_unpaid', 'purchased_plan'])}>
          <Row>
            {select('failed_payment', 'Failed payment', [['yes', 'Yes'], ['no', 'No']])}
            {number('failed_amount_min', 'Failed amount ≥ ₹')}
          </Row>
          <Row>
            {date('failed_from', 'Failed from')}
            {date('failed_to', 'Failed to')}
          </Row>
          <Row>
            {yesNo('ever_paid', 'Ever paid')}
            {text('purchased_plan', 'Last purchased plan')}
          </Row>
          <Row>
            {date('paid_from', 'Last paid from')}
            {date('paid_to', 'Last paid to')}
          </Row>
          <Row>
            {date('first_paid_from', 'First paid from')}
            {date('first_paid_to', 'First paid to')}
          </Row>
          <Row>
            {number('lifetime_paid_min', 'Lifetime paid ≥ ₹')}
            {number('lifetime_paid_max', 'Lifetime paid ≤ ₹')}
          </Row>
          <Row>
            {number('last_paid_min', 'Last payment ≥ ₹')}
            {number('last_paid_max', 'Last payment ≤ ₹')}
          </Row>
          <Row>
            {yesNo('active_link', 'Active payment link')}
            {onlyYes('coupon_unpaid', 'Coupon applied, not paid')}
          </Row>
        </Section>

        <Section title="Algo usage" count={groupCount(['live_algos_min', 'live_algos_max', 'paid_not_live', 'capital_min', 'capital_max', 'pnl_window', 'pnl_sign', 'pnl_min', 'pnl_max'])}>
          <Row>
            {number('live_algos_min', 'Live algos ≥')}
            {number('live_algos_max', 'Live algos ≤')}
          </Row>
          <Row>
            {number('capital_min', 'Capital in algos ≥ ₹')}
            {number('capital_max', 'Capital in algos ≤ ₹')}
          </Row>
          <Row>
            {select('pnl_window', 'Gross P&L period', [['all', 'All time'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days']])}
            {select('pnl_sign', 'In profit / loss', [['profit', 'In profit'], ['loss', 'In loss']])}
          </Row>
          <Row>
            {number('pnl_min', 'Gross P&L ≥ ₹')}
            {number('pnl_max', 'Gross P&L ≤ ₹')}
          </Row>
          <Row>
            {onlyYes('paid_not_live', 'Paid plan, no live algo')}
          </Row>
        </Section>

        <Section title="Calls & follow-ups" count={groupCount(['callback', 'callback_from', 'callback_to', 'callback_source', 'followup', 'never_contacted', 'not_contacted_days', 'contacted_from', 'contacted_to'])}>
          <Row>
            {select('callback', 'Callback requested', [['yes', 'Yes'], ['no', 'No']])}
            {select('callback_source', 'Callback from', (meta?.callback_sources || []).map((s) => [s, s.replace('callback_card_', '').replace(/_/g, ' ')]))}
          </Row>
          <Row>
            {date('callback_from', 'Callback from date')}
            {date('callback_to', 'Callback to date')}
          </Row>
          <Row>
            {select('followup', 'My follow-ups', [['overdue', 'Overdue'], ['today', 'Due today'], ['upcoming', 'Upcoming'], ['any', 'Has a follow-up'], ['none', 'No follow-up']])}
            {yesNo('never_contacted', 'Dialed', 'Never dialed', 'Dialed at least once')}
          </Row>
          <Row>
            {select('not_contacted_days', 'Not dialed in', [[3, '3 days'], [7, '7 days'], [15, '15 days'], [30, '30 days']])}
            {date('contacted_from', 'Dialed from date')}
          </Row>
        </Section>

        <Section title="Onboarding" count={groupCount(['kyc', 'demat_kyc', 'ddpi', 'onboarding_step'])}>
          <Row>
            {select('kyc', 'App KYC', [['complete', 'Complete'], ['open', 'Open'], ['rejected', 'Rejected']])}
            {yesNo('demat_kyc', 'Demat KYC done')}
          </Row>
          <Row>
            {yesNo('ddpi', 'DDPI done')}
            {select('onboarding_step', 'Stuck at step', (meta?.onboarding_steps || []).map((s) => [s, s.replace(/_/g, ' ')]))}
          </Row>
        </Section>

        <Section title="Source & ownership" count={groupCount(['installed_from', 'installed_to', 'webinar_from', 'webinar_to', 'app_user', 'webinar_user', 'webinar_type', 'webinar_stage', 'webinar_paid_min', 'source', 'referred_by', 'dealer', 'owners', 'unassigned'])}>
          <Row>
            {date('installed_from', 'Installed from')}
            {date('installed_to', 'Installed to')}
          </Row>
          <Row>
            {date('webinar_from', 'Webinar reg. from')}
            {date('webinar_to', 'Webinar reg. to')}
          </Row>
          <Row>
            {select('webinar_stage', 'Webinar stage', [['registered_only', 'Registered only'], ['paid', 'Paid, no app'], ['attended', 'Attended'], ['not_attended', 'Not attended'], ['app_joined', 'Joined app'], ['converted', 'Bought a plan']])}
            {select('webinar_type', 'Webinar type', [['main', 'Main'], ['app', 'App']])}
          </Row>
          <Row>
            {segment === 'webinar' ? yesNo('app_user', 'Has the app') : yesNo('webinar_user', 'Webinar user')}
            {number('webinar_paid_min', 'Webinar paid ≥ ₹')}
          </Row>
          <Row>
            {text('source', 'Source contains')}
            {text('referred_by', 'Referred by')}
          </Row>
          <Row>
            {select('dealer', 'Dealer', [['none', 'Our leads (no dealer)'], ['any', 'Any dealer'], ...(meta?.dealers || []).map((d) => [d, d])])}
          </Row>
          {isManager && (
            <Row>
              {multi('owners', 'Assigned to', members.map((m) => [m.owner, m.owner]))}
              {yesNo('unassigned', 'Assignment', 'Unassigned', 'Assigned')}
            </Row>
          )}
        </Section>

        {(customColumns || []).length > 0 && (
          <Section title="Your columns" count={cfCount}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
              {customColumns.filter((c) => c.name.toLowerCase() !== 'owner').map((c) => (
                c.type === 'dropdown' ? (
                  <TextField key={c.id} {...field} select label={c.name} value={cf[c.name] ?? ''} onChange={(e) => setCf(c.name, e.target.value)}>
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="__empty__">Not set</MenuItem>
                    {(c.dropdown_values || []).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </TextField>
                ) : (
                  <TextField key={c.id} {...field} label={c.name} value={cf[c.name] ?? ''} onChange={(e) => setCf(c.name, e.target.value)} placeholder="exact, or use % as wildcard" />
                )
              ))}
            </Box>
          </Section>
        )}
        <Box sx={{ height: 12 }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', pb: 'calc(12px + env(safe-area-inset-bottom))' }}>
        <Button fullWidth variant="outlined" onClick={() => setDraft({})}>Clear all</Button>
        <Button fullWidth variant="contained" onClick={() => onApply(cleanFilters(draft))}>Show results</Button>
      </Box>
    </Box>
  );

  return (
    <Drawer anchor={isMobile ? 'bottom' : 'right'} open={open} onClose={onClose} PaperProps={{ sx: isMobile ? { height: '92dvh', borderTopLeftRadius: 12, borderTopRightRadius: 12 } : {} }}>
      {body}
    </Drawer>
  );
};

export default LeadFilters;
